import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { Intent } from '@blueprintjs/core';
import { AuditingTable } from 'components/auditing';
import { NotificationToaster } from 'components/notifications';
import { EmployeeReviewsService } from 'services';
import { SetFiltering } from 'state/actions';

const reviewHistoryTableHeaders = ["When", "Who", "Field", "Old value", "New value"];

const noDataMessage = "There have been no changes to this review.";
const filterDateFormat = "YYYY-MM-DD";
const auditingDatesFilterName = "auditingDates";

// The completed review's change log. The date filter opens on the day the review was completed,
// so every change made since shows without the filter being widened. The API reads a date-only
// start as midnight UTC, so the filter opens on the completion's UTC date to include the completion.
export function ReviewHistory({ reviewId, completedDate }) {
    const dispatch = useDispatch();
    const [tableData, setTableData] = useState(null);
    const [tableRecordCount, setTableRecordCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [datesApplied, setDatesApplied] = useState(false);

    const initialStartDate = moment.utc(completedDate).format(filterDateFormat);

    // AuditingTable takes its opening range from the date range remembered across every history
    // screen, so that range is set to the completion date first and the table mounted only once it
    // holds. The table then keeps the range in step with the user's own changes.
    useEffect(() => {
        dispatch(SetFiltering(auditingDatesFilterName, {
            startDate: initialStartDate,
            endDate: moment().format(filterDateFormat)
        }));
        setDatesApplied(true);
    }, [initialStartDate]);

    const onTableChange = (pageSize, pageNumber, startDate, endDate) => {
        setLoading(true);
        EmployeeReviewsService.getReviewChangeLog(reviewId, pageSize, pageNumber, startDate, endDate)
            .then((r) => {
                setTableData(r.data);
                setTableRecordCount(r.totalCount);
            })
            .catch(() => {
                setTableData([]);
                NotificationToaster.show(Intent.DANGER, "Failed to load the review history. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    if (!datesApplied) {
        return null;
    }

    return (
        <AuditingTable
            data={tableData}
            totalCount={tableRecordCount}
            onChange={onTableChange}
            allowClear={false}
            loading={loading}
            tableHeaders={reviewHistoryTableHeaders}
            initialStartDate={initialStartDate}
            noDataMessage={noDataMessage}>
        </AuditingTable>
    );
}

ReviewHistory.propTypes = {
    reviewId: PropTypes.string.isRequired,
    completedDate: PropTypes.string.isRequired
};
